'use client';

import { useQuery } from '@tanstack/react-query';
import axiosInstance from '@libs/axios';
import { usePPJKContext } from '@contexts/PPJKContext';

interface UsageDashboardGraphParams {
  ppjkName?: string;
  /** A super admin's picked PPJK; the server ignores it for everyone else. */
  ppjkNpwp?: string;
  startDate?: string;
  endDate?: string;
}

interface UsageDashboardGraphItem {
  date: string;
  totalAju: number;
}

interface UsageDashboardGraphResponse {
  success: boolean;
  message: string;
  data: {
    data: UsageDashboardGraphItem[];
  };
}

async function fetchUsageDashboardGraph({
  ppjkName,
  ppjkNpwp,
  startDate,
  endDate,
  signal,
}: UsageDashboardGraphParams & { signal?: AbortSignal }): Promise<UsageDashboardGraphItem[]> {
  // Check for access token
  if (!window.localStorage.getItem('accessToken') && !document.cookie.includes('accessToken')) {
    console.warn('No access token available, skipping dashboard API call');
    return [];
  }

  const { data: response } = await axiosInstance.post<UsageDashboardGraphResponse>(
    '/api/v1/dashboard/usage-dashboard-graph',
    {
      ppjkName,
      ppjkNpwp,
      startDate,
      endDate,
    },
    { signal }
  );

  return response.data?.data ?? [];
}

export default function useGetUsageDashboardGraph(params?: UsageDashboardGraphParams) {
  const { getPpjkWithFallback } = usePPJKContext();

  const ppjkName = params?.ppjkName || getPpjkWithFallback() || undefined;

  const queryResult = useQuery({
    queryKey: [
      'usage-dashboard-graph',
      {
        ppjkName,
        ppjkNpwp: params?.ppjkNpwp,
        startDate: params?.startDate,
        endDate: params?.endDate,
      },
    ],
    queryFn: ({ signal }) =>
      fetchUsageDashboardGraph({
        ...params,
        ppjkName,
        signal,
      }),
    retry: 1,
    staleTime: 30 * 1000, // 30 seconds
  });

  return {
    data: queryResult.data,
    loading: queryResult.isLoading || queryResult.isFetching,
    error: queryResult.error?.message ?? '',
  };
}

export type { UsageDashboardGraphItem, UsageDashboardGraphParams };
